import { motion } from 'framer-motion';
import { Car, MapPin, Calendar, Plane, Navigation, CheckCircle, Info } from 'lucide-react';

const Pricing = () => {
  const pricingCategories = [
    {
      icon: Plane,
      title: 'Airport Pickup / Drop',
      description: 'Reliable airport transfer services',
      popular: false,
      items: [
        { label: 'Airport Pickup', price: '₹1,100 - ₹2,100' },
        { label: 'Airport Drop', price: '₹1,200 - ₹2,200' }
      ]
    },
    {
      icon: MapPin,
      title: 'Per KM Charges',
      description: 'Flexible pay-per-kilometer pricing',
      popular: true,
      items: [
        { label: 'One-Way Trip', price: '₹15 - ₹25 per km' },
        { label: 'Two-Way Trip', price: '₹11 - ₹18 per km' }
      ]
    },
    {
      icon: Calendar,
      title: 'Per Day Package',
      description: 'Full day rental (160 KM included)',
      popular: false,
      items: [
        { label: 'Daily Package', price: '₹2,500 - ₹5,000' },
        { label: 'Includes', price: '160 KM per day' }
      ]
    }
  ];

  const tourPackages = [
    { name: 'Mussoorie Tour', price: '₹2,500 - ₹4,000' },
    { name: 'Dehradun Tour', price: '₹2,500 - ₹4,000' },
    { name: 'Haridwar Tour', price: '₹2,500 - ₹4,000' },
    { name: 'Rishikesh Tour', price: '₹2,500 - ₹4,000' }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="pricing-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <p className="text-sm text-orange-500 font-semibold uppercase tracking-wider">Transparent Pricing</p>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
              Pricing & Packages
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Affordable rates with no hidden charges. Choose the package that suits your travel needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Pricing Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {pricingCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`pricing-card-${index}`}
                className={`relative bg-white p-8 rounded-3xl border-2 shadow-lg hover:-translate-y-2 transition-transform duration-300 ${
                  category.popular
                    ? 'border-orange-500 shadow-orange-100'
                    : 'border-slate-200 shadow-slate-100'
                }`}
              >
                {category.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-orange-500 text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl inline-block mb-6">
                  <category.icon className="w-8 h-8 text-blue-600" />
                </div>

                <h3 className="text-2xl font-bold text-slate-900 mb-2">{category.title}</h3>
                <p className="text-slate-600 mb-8">{category.description}</p>

                <div className="space-y-4">
                  {category.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center pb-4 border-b border-slate-100 last:border-0">
                      <span className="text-slate-700 font-medium">{item.label}</span>
                      <span className="text-blue-600 font-bold">{item.price}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Tour Packages Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-3xl border border-slate-200 shadow-lg"
            data-testid="tour-packages-section"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 rounded-xl">
                <Navigation className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-slate-900">Tour Packages</h3>
                <p className="text-slate-600">Per Day - Explore beautiful Uttarakhand destinations</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {tourPackages.map((tour, index) => (
                <div
                  key={index}
                  data-testid={`tour-package-${index}`}
                  className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-blue-200 transition-all duration-300"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <h4 className="font-bold text-slate-900">{tour.name}</h4>
                  </div>
                  <p className="text-orange-600 font-bold text-lg">{tour.price}</p>
                  <p className="text-sm text-slate-500 mt-2">per day</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Delhi Drop */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-8 bg-blue-600 p-10 rounded-3xl shadow-xl text-white"
            data-testid="delhi-drop-section"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                  <Car className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Delhi Drop Service</h3>
                  <p className="text-blue-100">Direct transfer to Delhi</p>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-3xl font-bold">₹3,500 - ₹6,000</p>
                <p className="text-blue-100 text-sm mt-1">One-way trip</p>
              </div>
            </div>
          </motion.div>

          {/* Pricing Note */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg"
            data-testid="pricing-note"
          >
            <div className="flex gap-4">
              <Info className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-amber-900 mb-2">Important Pricing Information</h4>
                <ul className="text-sm text-amber-800 space-y-2">
                  <li>• Prices may vary based on car type (Sedan, SUV, Tempo Traveller)</li>
                  <li>• Seasonal variations may apply during peak tourist season</li>
                  <li>• Toll charges, parking fees, and interstate taxes are extra</li>
                  <li>• All prices are inclusive of driver allowance</li>
                  <li>• Contact us for customized packages and group bookings</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 text-center"
          >
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Need a Custom Quote?
            </h3>
            <p className="text-slate-600 mb-8">
              Contact us for personalized pricing and special packages
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+916396867682"
                data-testid="pricing-call-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-orange-500/30 hover:-translate-y-0.5 uppercase tracking-wide"
              >
                Call: +91-6396867682
              </a>
              <a
                href="https://wa.me/916396867682"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pricing-whatsapp-btn"
                className="bg-green-500 hover:bg-green-600 text-white font-bold px-10 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-green-500/30 hover:-translate-y-0.5"
              >
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
